/**
 * The landing page: what this is, in the order someone needs it.
 *
 * The product is hard to describe abstractly and obvious from an example, so
 * the hero leads with two things a person would actually say to their
 * assistant, and the diagram below shows the one fact that makes them possible
 * — the model is looking at the user's own tab, through the extension they
 * installed, not at a page this server fetched.
 */

import type { ReactNode } from "react";
import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import { Link } from "react-router";
import { LinkButton } from "../components/ui";
import { DOCS_URL } from "../links";
import { useSession } from "../useSession";

const STEPS: Array<{ title: string; body: ReactNode }> = [
  {
    title: "Install",
    body: (
      <>
        Add the extension to Chrome — from{" "}
        <Link to="/install">this deployment's download</Link>, once you have signed in,
        or the store listing for your deployment.
      </>
    ),
  },
  {
    title: "Link",
    body: (
      <>
        Open the side panel and choose <strong>Link this browser</strong>. You sign in here once; the extension keeps a
        token you can revoke from <Link to="/account">Account</Link> at any time.
      </>
    ),
  },
  {
    title: "Talk",
    body: (
      <>
        Give your assistant the <Link to="/connect">MCP URL</Link> and describe what you want. It looks at your tab,
        previews the result in the page, and saves it.
      </>
    ),
  },
];

const FEATURES: Array<{ title: string; body: string; icon: ReactNode }> = [
  {
    title: "It can see the tab",
    body: "Snapshots, queries, text, screenshots and live extraction are answered by your own browser — so selectors are proven against the real page before anything is saved.",
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
        <path d="M1.6 10S4.8 4.4 10 4.4 18.4 10 18.4 10 15.2 15.6 10 15.6 1.6 10 1.6 10Z" />
        <circle cx="10" cy="10" r="2.6" />
      </svg>
    ),
  },
  {
    title: "Configuration, not code",
    body: "A feature is a JSON document: where it applies, what to read, when it runs, what happens. The extension interprets it — nothing is ever fetched and executed as script.",
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M7 3H4.6A1.6 1.6 0 0 0 3 4.6v10.8A1.6 1.6 0 0 0 4.6 17H13M7 3v14M7 3h8.4A1.6 1.6 0 0 1 17 4.6V13" />
        <path strokeLinecap="round" d="M10.2 6.6h3.6M10.2 9.6h3.6" />
      </svg>
    ),
  },
  {
    title: "Overlays, menus, captures",
    body: "Panels pinned where you want them, right-click items, keyboard shortcuts, and capture logs you can download as JSON, CSV or NDJSON.",
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
        <rect x="2.6" y="3.4" width="14.8" height="13.2" rx="2" />
        <path d="M2.6 7.4h14.8" />
        <path strokeLinecap="round" d="M10.6 11h4.2M10.6 13.6h4.2" />
      </svg>
    ),
  },
  {
    title: "Sandboxed logic",
    body: "When data cannot say it, the assistant writes a small module compiled to WebAssembly. It is instantiated with no imports: every single thing it touches is an effect the extension inspected first.",
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
        <path strokeLinejoin="round" d="M10 2.2 16.6 5.6v8.8L10 17.8 3.4 14.4V5.6Z" />
        <path strokeLinejoin="round" d="M10 9.2 16.6 5.6M10 9.2 3.4 5.6M10 9.2v8.6" />
      </svg>
    ),
  },
  {
    title: "You watch it work",
    body: "Every tool call your assistant makes is recorded and pushed live to the extension's Activity view — tool, one-line summary, whether it worked. Never the arguments.",
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.4 11h3l2-5.4 3 9.4 2.2-5.2 1.4 1.2h3.6" />
      </svg>
    ),
  },
  {
    title: "Revocable, always",
    body: "Linked browsers and assistant connections are rows you can delete. Revoking takes effect on the next call, not when a token expires.",
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
        <path strokeLinejoin="round" d="M10 2.4 16.4 5v5.2c0 3.4-2.6 6.2-6.4 7.4-3.8-1.2-6.4-4-6.4-7.4V5Z" />
        <path strokeLinecap="round" d="m7.6 10.2 1.8 1.8 3.4-3.8" />
      </svg>
    ),
  },
];

/**
 * How it fits together, drawn rather than described. Colours come from the
 * theme's custom properties, so it is correct in both palettes without a
 * second copy.
 */
function HowItWorks(): ReactNode {
  const box = { fill: "var(--emb-surface)", stroke: "var(--emb-border)", strokeWidth: 1.25, rx: 10 } as const;
  return (
    <svg
      className="emb-diagram"
      viewBox="0 0 760 268"
      role="img"
      aria-label="Your assistant calls the MCP endpoint on this Worker; the Worker asks your browser's extension over a WebSocket; the extension reads the tab you are looking at and answers."
    >
      <defs>
        <marker id="emb-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
          <path d="M0 0 10 5 0 10z" fill="var(--emb-accent)" />
        </marker>
      </defs>

      <g fontFamily="var(--bs-font-sans-serif)">
        {/* Assistant */}
        <rect x="8" y="46" width="168" height="78" {...box} />
        <text x="92" y="78" textAnchor="middle" fontSize="14" fontWeight="620" fill="var(--bs-body-color)">
          Your assistant
        </text>
        <text x="92" y="99" textAnchor="middle" fontSize="11.5" fill="var(--bs-secondary-color)">
          Claude · Cursor · ChatGPT
        </text>

        {/* Worker */}
        <rect x="240" y="46" width="168" height="78" {...box} />
        <text x="324" y="78" textAnchor="middle" fontSize="14" fontWeight="620" fill="var(--bs-body-color)">
          This Worker
        </text>
        <text x="324" y="99" textAnchor="middle" fontSize="11.5" fill="var(--bs-secondary-color)">
          OAuth · MCP · your hub
        </text>

        {/* Extension */}
        <rect x="472" y="46" width="168" height="78" {...box} />
        <text x="556" y="78" textAnchor="middle" fontSize="14" fontWeight="620" fill="var(--bs-body-color)">
          Your extension
        </text>
        <text x="556" y="99" textAnchor="middle" fontSize="11.5" fill="var(--bs-secondary-color)">
          in your own Chrome
        </text>

        {/* The tab */}
        <rect x="472" y="168" width="168" height="78" fill="var(--emb-accent-soft)" stroke="var(--emb-accent-line)" strokeWidth="1.25" rx="10" />
        <text x="556" y="200" textAnchor="middle" fontSize="14" fontWeight="620" fill="var(--bs-body-color)">
          The tab you are on
        </text>
        <text x="556" y="221" textAnchor="middle" fontSize="11.5" fill="var(--bs-secondary-color)">
          read, overlay, capture
        </text>

        {/* Arrows */}
        <g stroke="var(--emb-accent)" strokeWidth="1.6" fill="none" markerEnd="url(#emb-arrow)">
          <path d="M180 85h54" />
          <path d="M412 85h54" />
          <path d="M556 128v34" />
        </g>
        <text x="207" y="74" textAnchor="middle" fontSize="10.5" fill="var(--bs-secondary-color)">
          MCP
        </text>
        <text x="439" y="74" textAnchor="middle" fontSize="10.5" fill="var(--bs-secondary-color)">
          WebSocket
        </text>

        <text x="384" y="256" textAnchor="middle" fontSize="11.5" fill="var(--bs-secondary-color)">
          The Worker never loads your pages itself. Everything the model sees comes from the browser you linked.
        </text>
      </g>
    </svg>
  );
}

export function Landing(): ReactNode {
  const { account } = useSession();

  return (
    <>
      <section className="emb-hero">
        <Container>
          <Row className="align-items-center g-5">
            <Col lg={7}>
              <p className="emb-eyebrow mb-2">Chrome extension · MCP</p>
              <h1 className="mb-3">
                Tell your assistant how a website should behave. It builds it.
              </h1>
              <p className="fs-5 emb-muted mb-4" style={{ maxWidth: "36rem" }}>
                xtract.bot Embellisher is a Chrome extension whose features are written by your own LLM. It can look at
                the tab you have open, prove its selectors against the real page, show you a preview, and save the
                result — which the extension then applies, every time.
              </p>
              <div className="d-flex flex-wrap gap-2 mb-4">
                <LinkButton to={account ? "/connect" : "/signin?next=%2Fconnect"} size="lg">
                  {account ? "Connect an assistant" : "Get started"}
                </LinkButton>
                {/* Behind sign-in: the access list decides who gets the extension. */}
                <LinkButton to="/install" size="lg" variant="outline-primary">
                  Download the extension
                </LinkButton>
              </div>
              <p className="emb-muted small mb-0">
                Free to self-host. Sign in with GitHub, GitLab or Bitbucket — identity only, never repository access.
              </p>
            </Col>
            <Col lg={5}>
              <Card>
                <Card.Body>
                  <p className="emb-eyebrow mb-3">What you say</p>
                  <blockquote className="emb-quote">
                    When I'm on a sales order at <span className="emb-mono">abc.123.com</span>, show me the customer,
                    total and line items in a panel top-right.
                  </blockquote>
                  <blockquote className="emb-quote mb-0">
                    Add a right-click "Capture" that logs the product on the page as JSON, and another item to download
                    everything I've captured.
                  </blockquote>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      <Container className="py-5">
        <h2 className="h4 mb-4">Three steps</h2>
        <Row className="g-4">
          {STEPS.map((step, index) => (
            <Col md={4} key={step.title}>
              <Card className="h-100">
                <Card.Body className="d-flex flex-column gap-3">
                  <div className="d-flex align-items-center gap-3">
                    <span className="emb-step-num">{index + 1}</span>
                    <span className="h5 mb-0">{step.title}</span>
                  </div>
                  <p className="emb-muted mb-0">{step.body}</p>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      </Container>

      <Container className="py-5">
        <Card>
          <Card.Body className="p-4 p-lg-5">
            <h2 className="h4 mb-1">How it works</h2>
            <p className="emb-muted mb-4">One idea does most of the work: the model can see the tab.</p>
            <div className="d-flex justify-content-center">
              <HowItWorks />
            </div>
          </Card.Body>
        </Card>
      </Container>

      <Container className="py-5">
        <h2 className="h4 mb-4">What you get</h2>
        <Row className="g-4">
          {FEATURES.map((feature) => (
            <Col md={6} lg={4} key={feature.title}>
              <Card className="h-100">
                <Card.Body>
                  <span className="emb-feature-icon">{feature.icon}</span>
                  <h3 className="h6 mb-2">{feature.title}</h3>
                  <p className="emb-muted small mb-0">{feature.body}</p>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      </Container>

      <Container className="pb-5">
        <div className="emb-accent-panel p-4 p-lg-5 d-flex flex-column flex-lg-row gap-4 justify-content-between align-items-lg-center">
          <div>
            <h2 className="h5 mb-1">Ready when you are</h2>
            <p className="emb-muted mb-0">
              Install the extension, link this browser, and hand your assistant one URL. The design documents explain
              every part of it.
            </p>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <LinkButton to="/connect">Connect an assistant</LinkButton>
            <Button href={DOCS_URL} target="_blank" rel="noreferrer noopener" variant="outline-primary">
              Read the docs
            </Button>
          </div>
        </div>
      </Container>
    </>
  );
}
